// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- fallback for insecure contexts where crypto.randomUUID is unavailable
import { v4 as uuidv4 } from 'uuid';

import type { ExperienceEvent, FailEvent } from '@atlassian/experience-tracker/ExperienceEvent';
import type {
	CollectFn,
	ExperienceTrackerAPI,
} from '@atlassian/experience-tracker/ExperienceTracker';

import { ExperienceName } from './constants';

const randomId = (): string => {
	if (globalThis?.crypto?.randomUUID) {
		return globalThis.crypto.randomUUID();
	}

	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- fallback for insecure contexts where crypto.randomUUID is unavailable
	return uuidv4();
};

const SEND_MESSAGE_EXPERIENCE_NAMES = new Set<string>([ExperienceName.SEND_MESSAGE]);

const CREATE_TERMINAL_EXPERIENCE_NAMES = new Set<string>([
	ExperienceName.REMIX_RENDERS_IN_MODAL,
	ExperienceName.REMIX_INSERT,
	ExperienceName.REMIX_DUPLICATE_APP,
	ExperienceName.REMIX_DIAGRAM_PASTE_FINALIZE,
]);

export type RemixCreateAttributes = {
	entryPoint?: string;
	remixSource?: string;
	product?: string;
	remixType?: string;
	remixSubtype?: string;
	[key: string]: unknown;
};

type RemixCreateCollectContext = {
	/** The remix conversation id, used to correlate the send-message failure. */
	conversationChannelId?: string;
	/** A unique id generated when this REMIX_CREATE parent starts passed to all sub experiences */
	remixCreateId?: string;
	/** Attributes captured when the open-menu sub-experience starts. */
	openMenuAttributes?: RemixCreateAttributes;
	/** Prevents duplicate or late settlement when the overlay rerenders or the flow is aborted. */
	isOpenMenuSettled?: boolean;
	/** Prevents duplicate settlement when explicit cancellation is followed by component cleanup. */
	isCreateSettled?: boolean;
	allowCollectFallback?: boolean;
};

const toError = (error: Error | undefined): Error => error ?? new Error('aiMateSendMessage failed');

let activeCreateCollectContext: RemixCreateCollectContext | undefined;

const matchesTerminal = (event: ExperienceEvent, context: RemixCreateCollectContext): boolean => {
	if (!CREATE_TERMINAL_EXPERIENCE_NAMES.has(event.name)) {
		return false;
	}

	const attributes = event.attributes as Record<string, unknown> | undefined;

	const eventRemixCreateId = attributes?.remixCreateId;
	if (context.remixCreateId && typeof eventRemixCreateId === 'string') {
		return eventRemixCreateId === context.remixCreateId;
	}

	const eventConversationId = attributes?.conversationId;
	if (context.conversationChannelId && typeof eventConversationId === 'string') {
		return eventConversationId === context.conversationChannelId;
	}

	return Boolean(context.allowCollectFallback);
};

const matchesSendMessageFailure = (
	event: ExperienceEvent,
	context: RemixCreateCollectContext,
): boolean => {
	if (!SEND_MESSAGE_EXPERIENCE_NAMES.has(event.name) || event.action !== 'taskFail') {
		return false;
	}

	const attributes = event.attributes as Record<string, unknown> | undefined;
	if (attributes?.isRemixCreate !== true) {
		return false;
	}

	const requestConversationId = attributes?.conversationId;
	if (context.conversationChannelId && requestConversationId === context.conversationChannelId) {
		return true;
	}

	// If the event's conversationId is unavailable, fall back to the single-inflight
	// assumption (there is only one remix create stream at a time).
	return Boolean(context.allowCollectFallback);
};

/**
 * Creates a `collect` function for the REMIX_CREATE compound experience.
 *
 * Responsibilities:
 * - FAIL the parent when the correlated aiMateSendMessage request FAILS
 *   (correlated by conversationId, mirroring createRemixRegenerateCollect).
 * - FAIL / ABORT the parent when a terminal render/insert experience fails/aborts.
 * - SUCCEED the parent after a terminal experience succeeds AND no further terminal
 *   starts within a short quiet window (captures multi-step edit flows).
 *
 * The send-message flow itself is never modified — this only OBSERVES events.
 */
export const createRemixCreateCollect = (context: RemixCreateCollectContext): CollectFn => {
	let hasStopped = false;

	return (events, experience): void => {
		if (hasStopped || context.isCreateSettled) {
			hasStopped = true;
			return;
		}

		for (const event of events) {
			// 1) Upstream generation-stream failure → fail the parent.
			if (matchesSendMessageFailure(event, context)) {
				hasStopped = true;
				context.isCreateSettled = true;
				const attributes = event.attributes as Record<string, unknown> | undefined;
				experience.fail({
					error: toError((event as FailEvent).error),
					attributes: {
						...attributes,
						failureSource: 'send_message_collect',
						failureStage: 'request',
						upstreamExperience: event.name,
					},
				});
				return;
			}

			if (!matchesTerminal(event, context)) {
				continue;
			}

			const attributes = event.attributes as Record<string, unknown> | undefined;

			// 2) A terminal experience FAILED → fail the parent.
			if (event.action === 'taskFail') {
				hasStopped = true;
				context.isCreateSettled = true;
				experience.fail({
					error: toError((event as FailEvent).error),
					attributes: {
						...attributes,
						failureSource: 'terminal_collect',
						failureStage: 'render',
						upstreamExperience: event.name,
					},
				});
				return;
			}

			// 3) A terminal experience ABORTED → abort the parent.
			if (event.action === 'taskAbort') {
				hasStopped = true;
				context.isCreateSettled = true;
				experience.abort({
					reason: `terminal_${event.name}_aborted`,
					attributes: {
						...attributes,
						upstreamExperience: event.name,
					},
				});
				return;
			}

			// 4) A terminal experience SUCCEEDED → succeed the parent immediately.
			if (event.action === 'taskSuccess') {
				hasStopped = true;
				context.isCreateSettled = true;
				experience.succeed({
					attributes: {
						...attributes,
						succeededVia: event.name,
					},
				});
				return;
			}
		}
	};
};

/**
 * Starts the REMIX_CREATE compound (parent) experience at a remix entry point and,
 * for chooser-owned flows, its `open-menu` sub-experience. Direct-prompt flows can omit
 * `open-menu`. The parent's `collect` is wired to
 * {@link createRemixCreateCollect} so a correlated aiMateSendMessage failure fails
 * the parent, and a terminal render/insert experience settles it.
 *
 * The conversation does not exist yet at the entry point, so `collect` uses the
 * single-inflight fallback; call {@link updateRemixCreateConversationId} once the
 * conversation id is known to stamp it onto the running experience.
 */
export const startRemixCreateExperience = ({
	experienceTracker,
	attributes,
	includeOpenMenuExperience = true,
}: {
	experienceTracker: ExperienceTrackerAPI;
	attributes?: RemixCreateAttributes;
	includeOpenMenuExperience?: boolean;
}): void => {
	const remixCreateId = randomId();

	const startAttributes = { ...attributes, remixCreateId };

	const collectContext: RemixCreateCollectContext = {
		remixCreateId,
		...(includeOpenMenuExperience && { openMenuAttributes: startAttributes }),
		allowCollectFallback: true,
	};
	activeCreateCollectContext = collectContext;

	experienceTracker.start({
		name: ExperienceName.REMIX_CREATE,
		id: remixCreateId,
		attributes: startAttributes,
		collect: createRemixCreateCollect(collectContext),
	});
	if (includeOpenMenuExperience) {
		experienceTracker.start({
			name: ExperienceName.REMIX_OPEN_MENU,
			id: ExperienceName.REMIX_OPEN_MENU,
			attributes: startAttributes,
		});
	}
};

export const getActiveRemixCreateId = (): string | undefined =>
	activeCreateCollectContext?.isCreateSettled
		? undefined
		: activeCreateCollectContext?.remixCreateId;

/**
 * Succeeds the open-menu sub-experience once the Remix chooser has actually rendered.
 * A start without this success therefore identifies a flow where the expected UI did not open.
 */
export const succeedRemixOpenMenuExperience = (experienceTracker: ExperienceTrackerAPI): void => {
	const context = activeCreateCollectContext;
	if (!context?.openMenuAttributes || context.isOpenMenuSettled) {
		return;
	}

	context.isOpenMenuSettled = true;
	experienceTracker.succeed({
		name: ExperienceName.REMIX_OPEN_MENU,
		attributes: context.openMenuAttributes,
	});
};

/**
 * Succeeds REMIX_CREATE when generation is handed off to AIFC.
 * REMIX_AIFC_HANDOFF remains an independent capability experience rather than
 * being collected as a REMIX_CREATE sub-experience.
 */
export const succeedRemixCreateExperienceAtHandoff = (
	experienceTracker: ExperienceTrackerAPI,
	attributes?: RemixCreateAttributes,
): void => {
	const context = activeCreateCollectContext;
	if (!context || context.isCreateSettled) {
		return;
	}

	context.isCreateSettled = true;
	experienceTracker.succeed({
		name: ExperienceName.REMIX_CREATE,
		attributes: {
			...context.openMenuAttributes,
			...attributes,
			succeededVia: ExperienceName.REMIX_AIFC_HANDOFF,
		},
	});
};

/**
 * Aborts the REMIX_CREATE compound experience (and its non-gating open-menu
 * milestone) when the flow is abandoned before reaching a terminal — e.g. the
 * user closes the remix overlay/modal or cancels remix thinking without
 * committing an option.
 */
export const abortRemixCreateExperience = (
	experienceTracker: ExperienceTrackerAPI,
	reason: string,
): void => {
	const context = activeCreateCollectContext;
	if (!context) {
		return;
	}

	const attributes = { ...context.openMenuAttributes, reason };
	if (context.openMenuAttributes && !context.isOpenMenuSettled) {
		context.isOpenMenuSettled = true;
		experienceTracker.abort({
			name: ExperienceName.REMIX_OPEN_MENU,
			reason,
			attributes,
		});
	}

	if (!context.isCreateSettled) {
		context.isCreateSettled = true;
		experienceTracker.abort({
			name: ExperienceName.REMIX_CREATE,
			reason,
			attributes,
		});
	}
};

export const abortRemixCreateExperienceIfNotCommitted = (
	experienceTracker: ExperienceTrackerAPI,
	hasCommitted: boolean,
	reason = 'User closed remix overlay',
): void => {
	if (!hasCommitted) {
		abortRemixCreateExperience(experienceTracker, reason);
	}
};

/**
 * Records a non-gating milestone sub-experience (select-option / show-loading) as
 * the flow advances: an instantaneous start-then-succeed. These are funnel
 * breadcrumbs; the parent's success is owned by the terminal experiences via
 * `collect`, not by these milestones.
 */
const recordRemixCreateMilestone = (
	experienceTracker: ExperienceTrackerAPI,
	name: string,
	attributes?: RemixCreateAttributes,
): void => {
	const remixCreateId = getActiveRemixCreateId();
	const milestoneAttributes = { ...attributes, remixCreateId };
	experienceTracker.start({ name, id: name, attributes: milestoneAttributes });
	experienceTracker.succeed({ name, attributes: milestoneAttributes });
};

export const trackRemixCreateOptionSelected = ({
	experienceTracker,
	conversationId,
	remixType,
	remixSubtype,
	includesFreegenPrompt,
}: {
	experienceTracker: ExperienceTrackerAPI;
	conversationId: string | undefined;
	remixType: string | undefined;
	remixSubtype: string | undefined;
	includesFreegenPrompt?: boolean;
}): void => {
	if (conversationId) {
		updateRemixCreateConversationId(experienceTracker, conversationId);
	}
	const attributes = { remixType, remixSubtype, includesFreegenPrompt };
	experienceTracker.updateAttributes({ name: ExperienceName.REMIX_CREATE, attributes });
	recordRemixCreateMilestone(experienceTracker, ExperienceName.REMIX_SELECT_OPTION, attributes);
	recordRemixCreateMilestone(experienceTracker, ExperienceName.REMIX_SHOW_LOADING, attributes);
};

export const updateRemixCreateConversationId = (
	experienceTracker: ExperienceTrackerAPI,
	conversationId: string,
): void => {
	experienceTracker.updateAttributes({
		name: ExperienceName.REMIX_CREATE,
		attributes: { conversationId },
	});
	if (activeCreateCollectContext) {
		activeCreateCollectContext.conversationChannelId = conversationId;
	}
};
